class ProductPin extends HTMLElement {
  /** @type {string | undefined} */ #productId = this.dataset.productId;
  /** @type {'product' | 'image' | null} */ #type =
    this.closest('[data-pin-container]')?.getAttribute('data-pin-container');
  /** @type {HTMLElement | null} */ #section = this.closest('.shopify-section');
  /** @type {number} */ static PING_DURATION = 3000;
  /** @type {string} */ static PING_SELECTOR = '[data-ping]';

  connectedCallback() {
    if (!this.#productId || !this.#type) {
      this.#abort(`Product pin ${this.id} is missing product ID or type`);
    }

    if (!this.#getPairedPin(this.#section)) {
      this.#abort(`Product pin ${this.id} is missing a pair`, false);
    }

    this.addEventListener('click', this.#onClick.bind(this));
  }

  /**
   * Handle click event on the pin.
   * @returns {void}
   */
  #onClick() {
    const swiper = this.#getPairedSwiper();
    if (!swiper) {
      this.#abort(`Product pin ${this.id} is missing a corresponding swiper`);
    }

    this.#navigateToPair(swiper);
  }

  /**
   * Navigate the swiper to the slide containing the paired pin and trigger a ping.
   * @param {import('./services/swiper/index.js').default} swiper
   * @returns {void}
   */
  #navigateToPair(swiper) {
    /** @type {ProductPin | null} */
    let pairedPin;
    if (this.#type === 'product') {
      const currentSlide = swiper?.slides[swiper.activeIndex];
      pairedPin = this.#getPairedPin(currentSlide) || this.#getPairedPin(swiper?.el);
    } else {
      pairedPin = this.#getPairedPin(swiper?.el);
    }

    if (!pairedPin) return;

    const slide = pairedPin.closest('.swiper-slide');
    if (!slide) return;

    swiper.slideTo(swiper.slides.indexOf(slide));
    this.#ping(pairedPin);
  }

  /**
   * Briefly reveal the ping indicator element on the given pin.
   * @param {ProductPin} pin
   * @returns {void}
   */
  #ping(pin) {
    const pingEl = pin.querySelector(ProductPin.PING_SELECTOR);
    if (!pingEl) return;

    pingEl.classList.remove('hidden');
    setTimeout(() => pingEl.classList.add('hidden'), ProductPin.PING_DURATION);
  }

  /**
   * Get the corresponding Swiper instance associated with this pin's section.
   * @returns {import('./services/swiper/index.js').default | null}
   */
  #getPairedSwiper() {
    if (this.#type === 'product') {
      return this.#section?.querySelector('image-swiper')?.swiper;
    } else if (this.#type === 'image') {
      return this.#section?.querySelector('.product-swiper')?.swiper;
    }
    return null;
  }

  /**
   * Find a paired product-pin element within the given container.
   * @param {Element | HTMLElement | null | undefined} el
   * @returns {ProductPin | null}
   */
  #getPairedPin(el) {
    const pair = el?.querySelector(`product-pin[data-product-id="${this.#productId}"]`);
    return pair && pair !== this ? pair : null;
  }

  /**
   * Log a warning and remove this element from the DOM.
   * @param {string} message
   * @returns {void}
   */
  #abort(message, remove = true) {
    console.warn(message);
    if (remove) {
      this.remove();
    }
  }
}

if (!customElements.get('product-pin')) {
  customElements.define('product-pin', ProductPin);
}
